import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text } from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import BeautyInsiderRewardsSummaryEN from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/locales/BeautyInsiderRewardsSummary_en_US';
import BeautyInsiderRewardsSummaryFR from 'components/Content/BeautyInsider/BeautyInsiderRewardsSummary/locales/BeautyInsiderRewardsSummary_fr_CA';
import {
    fontSizes, lineHeights, fontWeights
} from 'style/config';
import Empty from 'constants/empty';

/**
 * Renders the left content for BeautyInsider Cash card based on available promotions
 * @param {Array} promotions - Array of available promotion objects with { points, localizedDiscountAmount }
 * @param {Object|null} displayedPromotion - The promotion to display (for single or 3+ promotions)
 * @param {string} [appliedDiscountAmount] - Discount amount of the applied promo (e.g. "$10.00")
 * @param {boolean} applied - If true, forces the full single-promotion display (value off + points) regardless of how many promotions are available
 */
const BeautyInsiderCashContent = ({
    promotions = Empty.Array,
    displayedPromotion,
    appliedDiscountAmount,
    applied = false
}) => {

    // Single or 3+ promotions: use displayedPromotion
    if (displayedPromotion) {
        const isSinglePromotion = appliedDiscountAmount || promotions.length === 1;

        return (
            <Box>
                <Text
                    is='p'
                    css={styles.title}
                >
                    {getText('biCashTitle')}
                </Text>

                {isSinglePromotion || applied ? (
                    <>
                        <Text
                            is='p'
                            css={styles.primaryText}
                        >
                            {getText('biCashValueOff', [displayedPromotion.localizedDiscountAmount])}
                        </Text>
                        <Text
                            is='p'
                            css={styles.secondaryText}
                        >
                            {getText('biCashForPoints', [displayedPromotion.points || 0])}
                        </Text>
                    </>
                ) : (
                    <Text
                        is='p'
                        css={styles.primaryText}
                    >
                        {getText('biCashUpToOff', [displayedPromotion.localizedDiscountAmount])}
                    </Text>
                )}
            </Box>
        );
    }

    // Two promotions: show title and both promotion details
    if (promotions.length === 2) {
        const promo1 = promotions[0];
        const promo2 = promotions[1];

        return (
            <Box css={styles.multiplePromoContainer}>
                <Text
                    is='p'
                    css={styles.title}
                >
                    {getText('biCashTitle')}
                </Text>
                <Text
                    is='p'
                    css={styles.secondaryText}
                >
                    {getText('biCashTwoPromotionsPrefix', [promo1.points || 0])}
                    <Text
                        is='span'
                        css={styles.boldText}
                    >
                        {getText('biCashTwoPromotionsSuffix', [promo1.localizedDiscountAmount])}
                    </Text>
                </Text>
                <Text
                    is='p'
                    css={styles.secondaryText}
                >
                    {getText('biCashTwoPromotionsPrefix', [promo2.points || 0])}
                    <Text
                        is='span'
                        css={styles.boldText}
                    >
                        {getText('biCashTwoPromotionsSuffix', [promo2.localizedDiscountAmount])}
                    </Text>
                </Text>
            </Box>
        );
    }

    // Fallback: no promotions or empty array
    return null;
};

const styles = {
    title: {
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight
    },
    primaryText: {
        fontSize: `${fontSizes.lg}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.base
    },
    secondaryText: {
        fontSize: `${fontSizes.base}px`,
        lineHeight: lineHeights.base
    },
    boldText: {
        fontWeight: fontWeights.bold
    },
    multiplePromoContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    }
};

const getText = localeUtils.getLocalisedDataFromResource(BeautyInsiderRewardsSummaryEN, BeautyInsiderRewardsSummaryFR);

export default wrapFunctionalComponent(BeautyInsiderCashContent, 'BeautyInsiderCashContent');
